import { useIonRouter } from '@ionic/react';
import type { Workspace } from '@up2stay/up2promo-api-client';
import { AuthApi } from '@up2stay/up2promo-api-client';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { shellPaths, TABS_PREFIX } from '@/core/router/app-paths';

import {
  fetchPlanStepIncomplete,
  planOnboardingGateFullQueryKey,
  planOnboardingGateQueryKey,
} from '@/core/components/onboarding/onboarding-plan-gate';
import {
  markAuthSessionValidated,
  resetAuthSessionFailureCount,
  resetAuthSessionRetryDelay,
} from '@/core/hooks/auth-session-cache';
import { useWorkspaces } from '@/core/hooks/use-workspaces';
import { AuthStatus, useAuthStore } from '@/core/store/auth.store';
import {
  useSelectedWorkspaceId,
  useWorkspaceStore,
} from '@/core/store/workspace.store';

/**
 * Paths that must not be yanked to `/onboarding` when the selected workspace
 * still needs plan confirmation (in-wizard create, auth, invitations, …).
 */
export const ONBOARDING_EXCLUDED_PREFIXES = [
  '/onboarding',
  '/workspace/invitation',
  '/auth',
  `${TABS_PREFIX}/more/user/`,
  shellPaths.moreConfirmNewEmail,
  shellPaths.moreWorkspaceCreate,
  '/more/user/',
  '/more/confirm-new-email',
];

export type WorkspaceBootstrapStatus =
  | 'loading'
  | 'ready'
  | 'needs-onboarding'
  | 'error';

const PROTECTED_ROUTE_PREFIXES = ['/tabs', '/more'];

function isShellProtectedPath(pathname: string): boolean {
  return PROTECTED_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export function isOnboardingExcludedPath(pathname: string): boolean {
  return ONBOARDING_EXCLUDED_PREFIXES.some((prefix) =>
    pathname.startsWith(prefix),
  );
}

type PlanGateState = 'idle' | 'loading' | 'required' | 'clear';

/**
 * After workspace create, Free looks identical in billing fields to “never
 * chose a plan”. Guidance Progress `planConfirmed`, active trial/sub, or an
 * active billing grant decides whether onboarding must resume on the plan step.
 */
function usePlanOnboardingGate(
  workspaces: Workspace[],
  selectedWorkspaceId: string | null,
  enabled: boolean,
): PlanGateState {
  const workspace =
    workspaces.find((ws) => ws.id === selectedWorkspaceId) ?? workspaces[0];

  const query = useQuery({
    queryKey: workspace
      ? planOnboardingGateFullQueryKey(workspace)
      : ([
          ...planOnboardingGateQueryKey('none'),
          'none',
          'none',
          'none',
        ] as const),
    queryFn: () => fetchPlanStepIncomplete(workspace as Workspace),
    enabled: enabled && Boolean(workspace),
    staleTime: 0,
  });

  if (!enabled || workspaces.length === 0) return 'clear';
  if (!workspace) return 'clear';
  if (query.isPending) return 'loading';
  if (query.data === true) return 'required';
  // Fail open on error so users are not locked out of the shell.
  return 'clear';
}

function useWorkspaceBootstrapStatusValue(
  workspacesState: ReturnType<typeof useWorkspaces>,
): WorkspaceBootstrapStatus {
  const user = useAuthStore((state) => state.user);
  const authStatus = useAuthStore((state) => state.authStatus);
  const authHydrated = useAuthStore((state) => state.hydrated);
  const accessToken = useAuthStore((state) => state.tokens?.accessToken);
  const workspaceHydrated = useWorkspaceStore((state) => state.hydrated);
  const selectedWorkspaceId = useSelectedWorkspaceId();
  const { workspaces, isPending, isFetching, isFetched, isError } =
    workspacesState;

  const membershipsReady =
    Boolean(user) &&
    workspaceHydrated &&
    isFetched &&
    !isError &&
    !(isPending || (isFetching && !isFetched));

  const planGate = usePlanOnboardingGate(
    workspaces,
    selectedWorkspaceId,
    membershipsReady && workspaces.length > 0,
  );

  return useMemo((): WorkspaceBootstrapStatus => {
    if (!user) {
      if (!authHydrated) {
        return 'loading';
      }

      if (!accessToken) {
        return 'ready';
      }

      if (authStatus === AuthStatus.OFFLINE) {
        return 'error';
      }

      return 'loading';
    }

    if (!workspaceHydrated) {
      return 'loading';
    }

    if (isPending || (isFetching && !isFetched)) {
      return 'loading';
    }

    if (isError) {
      return 'error';
    }

    if (!isFetched) {
      return 'loading';
    }

    if (workspaces.length === 0) {
      return 'needs-onboarding';
    }

    if (!selectedWorkspaceId) {
      return 'loading';
    }

    const workspaceExists = workspaces.some(
      (ws) => ws.id === selectedWorkspaceId,
    );
    if (!workspaceExists) {
      return 'loading';
    }

    if (planGate === 'idle' || planGate === 'loading') {
      return 'loading';
    }

    if (planGate === 'required') {
      return 'needs-onboarding';
    }

    return 'ready';
  }, [
    user,
    authStatus,
    authHydrated,
    accessToken,
    workspaceHydrated,
    isPending,
    isFetching,
    isFetched,
    isError,
    workspaces,
    selectedWorkspaceId,
    planGate,
  ]);
}

function useWorkspaceBootstrapEffectsValue(
  status: WorkspaceBootstrapStatus,
  workspacesState: ReturnType<typeof useWorkspaces>,
): void {
  const user = useAuthStore((state) => state.user);
  const workspaceHydrated = useWorkspaceStore((state) => state.hydrated);
  const selectedWorkspaceId = useSelectedWorkspaceId();
  const setWorkspace = useWorkspaceStore((state) => state.setWorkspace);
  const clearWorkspace = useWorkspaceStore((state) => state.clearWorkspace);
  const { workspaces, error, isFetched } = workspacesState;
  const location = useLocation();
  const router = useIonRouter();
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    if (!user || !workspaceHydrated || !isFetched) return;

    if (
      selectedWorkspaceId &&
      !workspaces.some((ws) => ws.id === selectedWorkspaceId)
    ) {
      clearWorkspace();
    }
  }, [
    user,
    workspaceHydrated,
    isFetched,
    selectedWorkspaceId,
    workspaces,
    clearWorkspace,
  ]);

  useEffect(() => {
    if (!user || !workspaceHydrated || !isFetched) return;
    const first = workspaces[0];
    if (workspaces.length > 0 && first && !selectedWorkspaceId) {
      setWorkspace(first);
    }
  }, [
    user,
    workspaceHydrated,
    isFetched,
    workspaces,
    selectedWorkspaceId,
    setWorkspace,
  ]);

  useEffect(() => {
    if (status !== 'needs-onboarding') {
      hasRedirectedRef.current = false;
      return;
    }

    if (!user || error) return;
    if (hasRedirectedRef.current) return;

    const isExcluded = isOnboardingExcludedPath(location.pathname);
    if (isExcluded) return;
    if (!isShellProtectedPath(location.pathname)) return;

    hasRedirectedRef.current = true;
    router.push('/onboarding', 'root', 'replace');
  }, [status, user, error, location.pathname, router]);
}

export function useWorkspaceBootstrap() {
  const workspacesState = useWorkspaces();
  const status = useWorkspaceBootstrapStatusValue(workspacesState);
  useWorkspaceBootstrapEffectsValue(status, workspacesState);

  const setUser = useAuthStore((state) => state.setUser);
  const setAuthStatus = useAuthStore((state) => state.setAuthStatus);

  const retry = useCallback(async () => {
    resetAuthSessionRetryDelay();
    resetAuthSessionFailureCount();

    const { user, tokens } = useAuthStore.getState();
    if (!user && tokens?.accessToken) {
      const response = await AuthApi.me();
      markAuthSessionValidated(tokens.accessToken);
      setUser(response.data);
      setAuthStatus(AuthStatus.AUTHENTICATED);
    }

    await workspacesState.refetch();
  }, [setAuthStatus, setUser, workspacesState]);

  return { status, retry };
}

/** Standalone bootstrap status for routes outside WorkspaceBootstrapGate. */
export function useWorkspaceBootstrapStatus(): WorkspaceBootstrapStatus {
  const workspacesState = useWorkspaces();
  return useWorkspaceBootstrapStatusValue(workspacesState);
}

/** Standalone bootstrap effects for routes outside WorkspaceBootstrapGate. */
export function useWorkspaceBootstrapEffects(): void {
  const workspacesState = useWorkspaces();
  const status = useWorkspaceBootstrapStatusValue(workspacesState);
  useWorkspaceBootstrapEffectsValue(status, workspacesState);
}
